// @givmo/fundraisers-module — the "Started by supporters" tab (FCM-WEB3; plan §FC-13).
//
// The fundraisers OTHER people started that benefit this organization: directly, through a collection
// or Cause fund it belongs to, or because one of them already reached it with a gift. Each row is the
// fundraiser's PUBLIC page projection and never more (`SupporterFundraiserOut`): cover, name, the
// public organizer byline, "Part of {campaign}", a Closed badge, and the fundraiser's OWN total exactly
// as its public page shows it (D-FCM-14) — its raised figure and donation count, or only its goal
// percent when the organizer hides statistics publicly, or nothing. Every row opens the PUBLIC page
// in a new tab; there is no lifecycle, message, contact or management affordance here.
//
// The tab lives on the Fundraisers page (RULED 2026-09-25: a tab, never a sidebar row), revealed only
// when the host flavor says `supporterFundraisers` (charity host, knob on). The read is the generated
// `GET /org/{org_ref}/fundraisers/by-supporters`: a route miss (404) answers for a dark backend, a class
// that lists none and a closed Charity Terms gate alike, so it renders the calm unavailable notice.
//
// ⚠ A COUNT IS A CLAIM. The api-client's envelope unwrap drops `meta`, so the tab's count is the first
// page's rows: exact up to a page, "25+" past it (the fetch-one-extra sentinel). It reads PAGE ONE's
// cache entry wherever the list is paged to, and it renders nothing while loading, on an error, when
// unavailable and at zero. Nothing here states or hints at fundraisers it is not showing.
//
// ⚠ Money is the server's decimal string through `formatFundraiserMoney`; the percent is the server's
// integer. Nothing is parsed, summed or recomputed, and no figure is ever totalled across rows.

import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { extractList, isGivmoError, sdk, type SupporterFundraiserOut } from '@givmo/api-client';
import { orgFundraisersListOrgSupporterFundraisersQueryKey } from '@givmo/api-client/query';
import { Badge, Button, Card, EmptyState, Icon, cn, testIdProp } from '@givmo/design-system';
import { OrgConsoleUnavailableNotice } from '@givmo/org-console';
import { ExternalLink, HeartHandshake, Megaphone } from 'lucide-react';
import { useFundraisersModule } from './fundraisers-context';
import { formatFundraiserMoney } from './fundraisers-decode';
import { FundraisersLoading, FundraisersReadState } from './fundraisers-state';

/** Rows per page. The request asks for one more: the next-page sentinel, since `meta` never arrives. */
export const SUPPORTER_PAGE_SIZE = 25;

/** Every word this tab shows, in one place (the `NEVER_IMPLY_HIDING` rail scans this file). */
export const SUPPORTER_FUNDRAISERS_COPY = {
  ownTab: 'Your fundraisers',
  tab: 'Started by supporters',
  lede: (orgName: string) =>
    `Fundraisers people have started for ${orgName}, directly or through a collection or Cause fund. Each one opens its public page.`,
  empty: (orgName: string) => `No one has started a fundraiser for ${orgName} yet.`,
  unavailable: 'Supporter fundraisers aren’t available for this organization here.',
  organizer: (name: string) => `Organized by ${name}`,
  campaign: (name: string) => `Part of ${name}`,
  raised: 'raised by this fundraiser',
  donations: (count: number) => `${count} ${count === 1 ? 'donation' : 'donations'}`,
  goal: 'of its goal',
  newTab: '(opens in a new tab)',
  closed: 'Closed',
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * The page's rows off the unwrapped list. A body that is not a list at all is an error, never an
 * empty page (an empty state there would be a claim about a read that did not answer); a row missing
 * the fields it is drawn and linked by is dropped.
 */
export function requireSupporterFundraisers(body: unknown): SupporterFundraiserOut[] {
  if (!Array.isArray(body) && !(isRecord(body) && Array.isArray(body.data))) {
    throw new Error('The supporter fundraiser list could not be read.');
  }
  return extractList<SupporterFundraiserOut>(body).filter(
    (row) =>
      isRecord(row) &&
      typeof row.id === 'string' &&
      typeof row.name === 'string' &&
      typeof row.public_url === 'string' &&
      row.public_url.length > 0,
  );
}

export function supporterFundraisersOptions(
  orgRef: string,
  offset = 0,
): UseQueryOptions<SupporterFundraiserOut[], Error> {
  const options = {
    path: { org_ref: orgRef },
    query: { 'page[limit]': SUPPORTER_PAGE_SIZE + 1, 'page[offset]': offset },
  };
  return {
    queryKey: orgFundraisersListOrgSupporterFundraisersQueryKey(options),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const { data } = await sdk.orgFundraisersListOrgSupporterFundraisers({
        ...options,
        signal,
        throwOnError: true,
      });
      return requireSupporterFundraisers(data);
    },
    // A route miss or a refusal is an ANSWER (the tab is unavailable, or not this person's), never a
    // blip: no retry. Anything else keeps the hosts' one retry.
    retry: (failureCount: number, error: Error) =>
      !(isGivmoError(error) && (error.status === 404 || error.status === 403)) && failureCount < 1,
  };
}

/**
 * The tab label's count off page one's rows: exact up to a page, "25+" past it; null (nothing drawn)
 * while unknown, on any failure and at zero.
 */
export function supporterCountLabel(rows: readonly unknown[] | undefined): string | null {
  if (!rows || rows.length === 0) return null;
  return rows.length > SUPPORTER_PAGE_SIZE ? `${SUPPORTER_PAGE_SIZE}+` : String(rows.length);
}

/** One page of the list; `enabled` is the page's own read gate. */
export function useSupporterFundraisers(offset: number, enabled: boolean) {
  const config = useFundraisersModule();
  return useQuery({
    ...supporterFundraisersOptions(config.orgRef ?? '', offset),
    enabled: enabled && config.orgRef !== null,
  });
}

/** The tab label's count pill — neutral, and absent whenever `label` is. */
export function SupporterTabCount({ label }: { label: string | null }) {
  if (label === null) return null;
  return (
    <span
      className="inline-flex h-20 min-w-20 items-center justify-center rounded-full bg-neutral-100 px-6 text-s12 font-w600 leading-none tabular-nums text-text-secondary"
      {...testIdProp('fundraisers-tab-supporters-count')}
    >
      {label}
    </span>
  );
}

/**
 * The tab's content: the lede, then the rows in one card (Previous/Next beneath past one page), the
 * in-tab empty state, or the read's own failure.
 */
export function SupporterFundraisersPanel({
  offset,
  onOffset,
}: {
  offset: number;
  onOffset: (offset: number) => void;
}) {
  const config = useFundraisersModule();
  const copy = SUPPORTER_FUNDRAISERS_COPY;
  const query = useSupporterFundraisers(offset, true);
  if (query.isError) {
    return isGivmoError(query.error) && query.error.status === 404 ? (
      <OrgConsoleUnavailableNotice
        testId="supporter-fundraisers-unavailable"
        title={copy.tab}
        description={copy.unavailable}
      />
    ) : (
      <FundraisersReadState error={query.error} onRetry={() => void query.refetch()} />
    );
  }
  const rows = query.data?.slice(0, SUPPORTER_PAGE_SIZE) ?? [];
  const hasNext = (query.data?.length ?? 0) > SUPPORTER_PAGE_SIZE;
  return (
    <div className="flex flex-col gap-16" {...testIdProp('supporter-fundraisers')}>
      <p className="text-s14 text-text-secondary" {...testIdProp('supporter-fundraisers-lede')}>
        {copy.lede(config.orgName)}
      </p>
      {query.isPending ? (
        <FundraisersLoading />
      ) : rows.length === 0 ? (
        <Card testId="supporter-fundraisers-card">
          <EmptyState
            testId="supporter-fundraisers-empty"
            icon={<Icon icon={HeartHandshake} size={20} aria-hidden />}
            title={copy.empty(config.orgName)}
          />
        </Card>
      ) : (
        <Card testId="supporter-fundraisers-card">
          <ul
            className="divide-y divide-border-subtle"
            {...testIdProp('supporter-fundraisers-list')}
          >
            {rows.map((row) => (
              <li key={row.id}>
                <SupporterFundraiserRow row={row} />
              </li>
            ))}
          </ul>
        </Card>
      )}
      {(offset > 0 || hasNext) && (
        <div
          className="flex items-center justify-between gap-12"
          {...testIdProp('supporter-fundraisers-pagination')}
        >
          <Button
            appearance="saas"
            tone="secondary"
            disabled={offset === 0}
            onClick={() => onOffset(Math.max(0, offset - SUPPORTER_PAGE_SIZE))}
            testId="supporter-fundraisers-previous"
          >
            Previous
          </Button>
          <span className="text-s13 text-text-secondary">
            Page {Math.floor(offset / SUPPORTER_PAGE_SIZE) + 1}
          </span>
          <Button
            appearance="saas"
            tone="secondary"
            disabled={!hasNext}
            onClick={() => onOffset(offset + SUPPORTER_PAGE_SIZE)}
            testId="supporter-fundraisers-next"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

/** "Organized by {name} · Part of {campaign}", either half alone, or no line at all. */
export function supporterByline(row: SupporterFundraiserOut): string | null {
  const copy = SUPPORTER_FUNDRAISERS_COPY;
  const organizer = row.organizer?.name?.trim();
  const campaign = row.campaign?.campaign_name?.trim();
  const parts = [
    organizer ? copy.organizer(organizer) : null,
    campaign ? copy.campaign(campaign) : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function SupporterFundraiserRow({ row }: { row: SupporterFundraiserOut }) {
  const copy = SUPPORTER_FUNDRAISERS_COPY;
  const byline = supporterByline(row);
  return (
    <a
      href={row.public_url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'grid grid-cols-[40px_minmax(0,1fr)] items-center gap-x-12 gap-y-6 px-16 py-12 transition-colors hover:bg-surface-hover',
        'sm:grid-cols-[40px_minmax(0,1fr)_auto] sm:gap-x-16 sm:px-20',
        'outline-none focus-visible:bg-surface-hover focus-visible:shadow-ring',
      )}
      {...testIdProp(`supporter-fundraiser-${row.id}`)}
    >
      <SupporterCover src={row.image_url ?? null} />
      <div className="min-w-0">
        <p className="flex min-w-0 items-center gap-8">
          <span className="truncate text-s14 font-w600 text-text-strong">{row.name}</span>
          <Icon icon={ExternalLink} size={14} aria-hidden className="shrink-0 text-icon-muted" />
          <span className="sr-only">{copy.newTab}</span>
          {row.state === 'closed' && (
            <Badge
              tone="neutral"
              className="shrink-0"
              testId={`supporter-fundraiser-${row.id}-closed`}
            >
              {copy.closed}
            </Badge>
          )}
        </p>
        {byline !== null && (
          <p className="mt-2 text-s13 text-text-secondary sm:truncate">{byline}</p>
        )}
      </div>
      <SupporterMoney row={row} />
    </a>
  );
}

/**
 * The fundraiser's own figure, as its public page shows it: raised and count, else the goal percent,
 * else nothing at all.
 */
function SupporterMoney({ row }: { row: SupporterFundraiserOut }) {
  const copy = SUPPORTER_FUNDRAISERS_COPY;
  const line =
    'col-start-2 text-s13 text-text-secondary sm:col-start-3 sm:row-start-1 sm:text-right';
  const figure = 'font-w600 tabular-nums text-text-strong sm:block sm:text-s14';
  if (typeof row.raised === 'string' && row.raised.length > 0) {
    return (
      <p className={line} {...testIdProp(`supporter-fundraiser-${row.id}-raised`)}>
        <span className={figure}>{formatFundraiserMoney(row.raised)}</span> {copy.raised}
        {typeof row.donation_count === 'number' && Number.isFinite(row.donation_count) && (
          <>
            {' · '}
            {copy.donations(row.donation_count)}
          </>
        )}
      </p>
    );
  }
  if (typeof row.goal_progress_percent === 'number' && Number.isFinite(row.goal_progress_percent)) {
    return (
      <p className={line} {...testIdProp(`supporter-fundraiser-${row.id}-goal`)}>
        <span className={figure}>{row.goal_progress_percent}%</span> {copy.goal}
      </p>
    );
  }
  return null;
}

/** A missing cover is a bordered neutral tile: artwork that ends on its own edge, never the row's. */
function SupporterCover({ src }: { src: string | null }) {
  return src ? (
    <img src={src} alt="" className="size-[40px] rounded-md object-cover" />
  ) : (
    <span className="flex size-[40px] items-center justify-center rounded-md border border-border bg-neutral-50 text-icon-muted">
      <Icon icon={Megaphone} size={16} aria-hidden />
    </span>
  );
}
